import React, { useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Switch,
	Typography
} from '@mui/material';
import { CustomizedDataGrid } from '../customized_data_grid';
import DialogueModal from 'components/Modal/DialogModal';
import SilenceModal from './SilenceModal';
import { Column } from 'react-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteConfirmationDialog from './DeleteModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SilenceRow } from './createAlert';
import { useTheme } from '@mui/material/styles';

const startTimeFormatter = ({ row }: any) => {
  const date = row.startTime ? new Date(row.startTime) : new Date();
  return (
    <span>
      {!isNaN(date.getTime()) ? date.toISOString() : 'Invalid Date'}
    </span>
  );
};

const endTimeFormatter = ({ row }: any) => {
  const date = row.endTime ? new Date(row.endTime) : new Date();
  return (
    <span>
      {date && !isNaN(date.getTime()) ? date.toISOString() : 'Invalid Date'}
    </span>
  );
};

const silencedChannelsFormatter = ({ row }: any) => (
  <span>{row.silencedChannels.join(', ')}</span>
);

const SilenceStatusCell = ({ row }: any) => {
  const theme = useTheme();
  const colors = theme.palette;

  return (
    <Chip
      sx={{
        minWidth: 64,
        color: row.isEnabled
          ? `${colors.boxColor.pgmGreen.color} !important`
          : `${colors.boxColor.redTrend.color} !important`,
        backgroundColor: row.isEnabled
          ? `${colors.boxColor.greenTrend.bgColor} !important`
          : `${colors.boxColor.redTrend.bgColor} !important`,
        marginTop: 0.5
      }}
      label={row.isEnabled ? 'ACTIVE' : 'INACTIVE'}
    />
  );
};

const silenceStatusFormatter = ({ row }: any) => (
  <SilenceStatusCell row={row} />
);

const SilenceActionCell = ({
  row,
  maintenanceWindows,
  setMaintenanceWindows,
  setSelectedSilence,
  setShowSilenceModal
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleStatus = () => {
    setMaintenanceWindows(
      maintenanceWindows.map((mw: SilenceRow) =>
        mw.uuid === row.uuid ? { ...mw, isEnabled: !mw.isEnabled } : mw
      )
    );
    handleClose();
  };

  const handleAction = (actionType: string) => {
    setSelectedSilence(row);

    if (actionType === 'edit') {
      setShowSilenceModal(true);
    }

    if (actionType === 'delete') {
      setOpenDialog(true);
    }

    handleClose();
  };

  const handleDeleteConfirm = () => {
    setMaintenanceWindows((mws: SilenceRow[]) =>
      mws.filter((mw: SilenceRow) => mw.uuid !== row.uuid)
    );
    setOpenDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        aria-label="edit"
        size="small"
        onClick={() => handleAction('edit')}
        sx={{
          color: `${colors.table.moreIcon} !important`
        }}
      >
        <EditIcon
          fontSize="small"
          sx={{
            color: `${colors.table.moreIcon} !important`
          }}
        />
      </IconButton>

      <IconButton
        aria-label="more"
        size="small"
        onClick={handleClick}
        sx={{
          color: `${colors.table.moreIcon} !important`
        }}
      >
        <MoreVertIcon
          fontSize="small"
          sx={{
            color: `${colors.table.moreIcon} !important`
          }}
        />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              '& .MuiMenu-list': {
                paddingTop: 0,
                paddingBottom: 0,
              },
            },
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: `${colors.moreOptionDropdown?.background} !important`,
            color: `${colors.moreOptionDropdown?.menuItemText} !important`,
            borderRadius: '6px',
            boxShadow: `
              -2px 0 6px rgba(0, 0, 0, 0.2),
              2px 0 6px rgba(0, 0, 0, 0.2),
              0 4px 8px rgba(0, 0, 0, 0.25)
            `,
            minWidth: 160,
          },
        }}
      >
        <MenuItem
          onClick={handleToggleStatus}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: `${colors.moreOptionDropdown?.menuItemText} !important`,
            minHeight: '48px !important',
            py: 0,
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 32,
            }}
          >
            <Switch
              checked={row.isEnabled}
              size="small"
              color="default"
              sx={{
                pointerEvents: 'none',
                width: 34,
                height: 20,
                padding: 0,

                '& .MuiSwitch-switchBase': {
                  padding: '3px',
                  color: colors.toggle.thumb,

                  '&.Mui-checked': {
                    transform: 'translateX(14px)',
                    color: colors.toggle.thumb,

                    '& + .MuiSwitch-track': {
                      backgroundColor: colors.toggle.trackOn,
                      opacity: 1,
                    },
                  },

                  '&.Mui-disabled + .MuiSwitch-track': {
                    backgroundColor: colors.toggle.disabledTrack,
                    opacity: 1,
                  },
                },

                '& .MuiSwitch-thumb': {
                  width: 14,
                  height: 14,
                  backgroundColor: colors.toggle.thumb,
                  boxShadow: colors.toggle.thumbShadow,
                },

                '& .MuiSwitch-track': {
                  borderRadius: '10px',
                  backgroundColor: colors.toggle.trackOff,
                  opacity: 1,
                },
              }}
            />
          </ListItemIcon>

          <Box component="span">
            {row.isEnabled ? 'Deactivate' : 'Activate'}
          </Box>
        </MenuItem>

        <Divider
          sx={{
            margin: '0 !important',
            backgroundColor: `${colors.widget.text.noChange}`,
          }}
        />

        <MenuItem
          onClick={() => handleAction('delete')}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
            minHeight: '48px !important',
            py: 0,
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 32,
            }}
          >
            <DeleteOutlineIcon
              sx={{
                fontSize: '22px',
                color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
              }}
            />
          </ListItemIcon>

          <Box
            component="span"
            sx={{
              fontSize: '14px',
              color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
            }}
          >
            Delete
          </Box>
        </MenuItem>
      </Menu>

      <DeleteConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDeleteConfirm}
        identifierName={row.name || ''}
        type="Silence"
      />
    </Box>
  );
};

const silenceActionFormatter =
  (
    maintenanceWindows: SilenceRow[],
    setMaintenanceWindows: any,
    setSelectedSilence: any,
    setShowSilenceModal: any
  ) =>
  ({ row }: any) =>
    (
      <SilenceActionCell
        row={row}
        maintenanceWindows={maintenanceWindows}
        setMaintenanceWindows={setMaintenanceWindows}
        setSelectedSilence={setSelectedSilence}
        setShowSilenceModal={setShowSilenceModal}
      />
    );


export const SilencesTab = ({ maintenanceWindows = [], setMaintenanceWindows }: any) => {
    const [showSilenceModal, setShowSilenceModal] = useState(false);
    const [selectedSilence, setSelectedSilence] = useState<SilenceRow | null>(null);
    const defaultColumnSilence = ['Silence Name', 'Start Time (UTC)', 'End Time (UTC)', 'Silenced Channels', 'Status', 'Action']

    const handleManageSilences = (silence: SilenceRow, state: string) => {
        setSelectedSilence(null)
		if(state === 'add'){
			setMaintenanceWindows([...maintenanceWindows, silence])
		}else if(state === 'update'){
			setMaintenanceWindows(maintenanceWindows.map((sl:SilenceRow) => {
				return sl.uuid === silence.uuid ? silence : sl
			}))
		}
		setShowSilenceModal(false)
	};

    function getColumnsSilence(): Array<Column<SilenceRow>> {
		return [
			{
				key: 'name',
				name: 'Silence Name',
				minWidth: 150,
				width: 308
			},
			{
				key: 'startTime',
				name: 'Start Time (UTC)',
				width: 200,
				minWidth: 150,
				formatter: startTimeFormatter
			},
			{
				key: 'endTime',
				name: 'End Time (UTC)',
				minWidth: 95,
				formatter: endTimeFormatter
			},
			{
				key: 'silencedChannels',
				name: 'Silenced Channels',
				width: 200,
				minWidth: 150,
				formatter: silencedChannelsFormatter
			},
			{
				key: 'isEnabled',
				name: 'Status',
				width: 120,
				formatter: silenceStatusFormatter
			},
			{
				key: 'action',
				name: 'Action',
				formatter: silenceActionFormatter(
					maintenanceWindows,
					setMaintenanceWindows,
					setSelectedSilence,
					setShowSilenceModal
				),
				width: 90
			}
		];
	}

    return (
        <Box sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.7 }} color="textLight.main">
                    ⓘ This section is to create multiple silences for the alert.
                </Typography>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setShowSilenceModal(true)}
                >
                    CREATE SILENCE
                </Button>
            </Box>
            <CustomizedDataGrid
                tableColumns={getColumnsSilence()}
                tableData={maintenanceWindows}
                tableType="Silence"
                defaultColumnNames={defaultColumnSilence}
                listLength={maintenanceWindows?.length || 0}
                isDateRangeFilter={false}
                showExport={false}
                isShowMoreButton={false}
                page={1}
                pageSize={0}
                paginationData={0}
            />
            <DialogueModal
                title={selectedSilence ? 'Edit Silence' : 'Create Silence'}
                isOpen={showSilenceModal}
                handleClose={() => {
                    setShowSilenceModal(false);
                    setSelectedSilence(null);
                }}
            >
                <SilenceModal
                    selectedSilence={selectedSilence}
                    handleClose={() => {
                        setShowSilenceModal(false);
                        setSelectedSilence(null);
                    }}
                    onManageSilences={handleManageSilences}
                />
            </DialogueModal>
        </Box>
    )
}